import axios from "axios";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { FaUserAlt } from "react-icons/fa";
import { MdModeEdit } from "react-icons/md";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import PageBanner from "../Components/PageBanner";
import { useAuthContext } from "../Contexts/AuthContext";
import { toast, ToastContainer } from "react-toastify";

/* ------------------------------------------------------------------ *
 * Field definitions — one source of truth for labels and validation.
 * The old code counted Object.values(data) and compared to 15/16, which
 * broke every time a schema field was added (userdata carries _id, email,
 * password, uid, img, docImages… all of which have .length).
 * ------------------------------------------------------------------ */

const REQUIRED_FIELDS = [
  ["name", "Nombres"],
  ["surname", "Apellidos"],
  ["dob", "Fecha de nacimiento"],
  ["phone", "Teléfono"],
  ["doctype", "Tipo de documento"],
  ["docId", "Número de documento"],
  ["direction", "Dirección"],
  ["homenumber", "Número"],
  ["homevia", "Población / Provincia"],
  ["postalcode", "Código postal"],
  ["residence", "País"],
  ["occupation", "Ocupación"],
];

/** DD-MM-YYYY, and a real date that isn't in the future. */
const validDob = (v) => {
  const m = String(v || "").match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return false;
  const [, d, mo, y] = m.map(Number);
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d)
    return false;
  return dt < new Date();
};

/** At least 6 digits once symbols and spaces are stripped. */
const validPhone = (v) => String(v || "").replace(/\D/g, "").length >= 6;

/**
 * Defined at module scope on purpose. When these lived inside Profile(), every
 * keystroke produced a NEW component type, so React unmounted and remounted the
 * subtree and the input lost focus after each character.
 */
const Field = ({ id, label, hint, error, children }) => (
  <div className="tv-f">
    <label className="tv-l" htmlFor={id}>
      {label}
    </label>
    {children}
    {error ? (
      <p className="tv-err">{error}</p>
    ) : hint ? (
      <p className="tv-hint">{hint}</p>
    ) : null}
  </div>
);

const Row = ({ label, value }) => (
  <div className="tv-row">
    <dt>{label}</dt>
    <dd>{value || <span className="tv-empty">Sin completar</span>}</dd>
  </div>
);

const Profile = () => {
  const location = useLocation();
  const { userdata, setuserData, user } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [showModal, setModal] = useState(false);
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [docStatus, setDocStatus] = useState(null);
  const navigate = useNavigate();

  const HandleChange = (e) => {
    setData((prev) => ({ ...prev, [e.target.id]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.id]: null }));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setModal(params.get("edit") === "true");
  }, [location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* ---------------------------------------------------------------- *
   * Validation
   * ---------------------------------------------------------------- */
  const validate = () => {
    const next = {};

    REQUIRED_FIELDS.forEach(([key, label]) => {
      if (!String(data?.[key] ?? "").trim()) next[key] = `${label} es obligatorio`;
    });

    if (data?.dob && !validDob(data.dob))
      next.dob = "Usa el formato DD-MM-AAAA";

    // Free-text phones were reaching the payment provider and being rejected
    if (data?.phone && !validPhone(data.phone))
      next.phone = "Introduce un teléfono válido";

    if (data?.postalcode && !/^\d{4,10}$/.test(String(data.postalcode).trim()))
      next.postalcode = "Solo números";

    setErrors(next);
    return next;
  };

  const HandleEdit = async () => {
    const next = validate();
    const missing = Object.keys(next);

    if (missing.length) {
      const first = document.getElementById(missing[0]);
      if (first) first.scrollIntoView({ block: "center", behavior: "smooth" });
      toast.error(
        missing.length === 1
          ? next[missing[0]]
          : `Faltan ${missing.length} campos por completar`,
        { position: "top-center", theme: "dark" }
      );
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        process.env.REACT_APP_BASE_URL + `/api/auth/update/${userdata._id}`,
        { newUser: { ...userdata, ...data } },
        {
          headers: {
            "Content-Type": "application/json",
            token: Cookies.get("token"),
          },
        }
      );
      await getUser();
      setModal(false);
      toast.success("Datos actualizados", {
        position: "top-center",
        theme: "dark",
      });
    } catch (err) {
      console.error(err);
      toast.error("No se pudieron guardar los datos. Inténtalo de nuevo.", {
        position: "top-center",
        theme: "dark",
      });
    } finally {
      // Ran only on success before, so a failed save left the button stuck
      setLoading(false);
    }
  };

  const getUser = async () => {
    const res = await axios.post(
      process.env.REACT_APP_BASE_URL + "/api/auth/user",
      {},
      {
        headers: {
          "Content-Type": "application/json",
          token: Cookies.get("token"),
        },
      }
    );
    setuserData(res.data);
  };

  useEffect(() => {
    getUser();
    setData(userdata);
    setData((prev) => ({
      ...prev,
      residence: userdata.residence ? userdata.residence : "España",
    }));
  }, []);

  const countries = [
    "Alemania", "Bélgica", "Croacia", "Dinamarca", "España", "Francia",
    "Irlanda", "Letonia", "Luxemburgo", "Países Bajos", "Suecia", "Bulgaria",
    "Eslovaquia", "Estonia", "Grecia", "Malta", "Polonia", "República Checa",
    "Austria", "Chipre", "Eslovenia", "Finlandia", "Hungría", "Italia",
    "Lituania", "Portugal", "Rumanía",
  ];

  const occupations = [
    "Administrativo/a",
    "Agente de remesas",
    "Agricultura",
    "Albañilería",
    "Ama de Casa",
    "Asociaciones sin ánimo de lucro y religiosas",
    "Carpintería",
    "Comercial - Ventas",
    "Conductor/a - Chofer - Camionero/a",
    "Cuidado de personas / niños",
    "Dependiente/a - Cajero/a",
    "Desempleado/a - Sin actividad",
    "Directivo constructora / inmobiliaria",
    "Directivo/socio juegos de azar, casino y apuestas",
    "Directivo/socio sector joyería y oro",
    "Empleado/a de hogar",
    "Estudiante",
    "Fontanería",
    "Funcionario",
    "Hostelería (camarero/a)",
    "Limpieza",
    "Mecánico/a",
    "Militar",
    "Minería",
    "Notario",
    "Ocio Nocturno",
    "Operario/a de fábrica",
    "Peluquería",
    "Pensionista / Jubilado",
    "Pintor/a",
    "Propietario/a de bar/comercio",
    "Sector Armamentístico",
    "Sector Educación",
    "Sector industrial",
    "Sector sanitario",
    "Servicio Doméstico",
    "Trabajador de juegos de azar, casino y apuestas",
    "Trabajador sector joyería, oro y piedras preciosas",
    "Trabajador/a de la Construcción",
    "Transportista",
    "Otras Actividades",
  ];

  /** Auto-inserts the dashes so people don't have to type them. */
  const HandleDOBChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}-${digits.slice(2)}`;
    setData((prev) => ({ ...prev, dob: out }));
    setErrors((prev) => ({ ...prev, dob: null }));
  };

  const DeleteAcc = async () => {
    const email = window.prompt("Indique su Email");
    if (!email) return;
    const password = window.prompt("Introduzca su clave");
    if (!password) return;

    try {
      await axios.post(
        process.env.REACT_APP_BASE_URL + "/api/auth/delacc",
        { email, password },
        {
          headers: {
            "Content-Type": "application/json",
            token: Cookies.get("token"),
          },
        }
      );

      toast.success("Cuenta eliminada", { position: "top-center", theme: "dark" });
      Cookies.remove("token");
      navigate("/login");
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "No se pudo eliminar la cuenta", {
        position: "top-center",
        theme: "dark",
      });
    }
  };

  // Which ID images are on file. This replaces the old Neocheck video-ID
  // status — identity is now evidenced by the document images themselves.
  useEffect(() => {
    const loadDocs = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/pmi/document/status`,
          {
            headers: {
              "Content-Type": "application/json",
              token: Cookies.get("token"),
            },
          }
        );
        setDocStatus(data);
      } catch (_) {
        /* non-fatal — the payment page asks for the document anyway */
      }
    };
    if (Cookies.get("token")) loadDocs();
  }, []);

  if (!user) return <Navigate to={"/"} />;

  /* ---------------------------------------------------------------- *
   * Render helpers
   * ---------------------------------------------------------------- */

  const filled = REQUIRED_FIELDS.filter(([k]) =>
    String(userdata?.[k] ?? "").trim()
  ).length;
  const isComplete = filled === REQUIRED_FIELDS.length;

  const docsOnFile = Boolean(docStatus?.complete);
  const DOC_LABELS = { dni: "DNI", nie: "NIE", per: "Permiso de residencia", pass: "Pasaporte" };

  return (
    <div className="ProfileSection tv-prof">
      <style>{`
        .tv-prof {
          --accent: #F6394F;
          --accent-strong: #e22440;
          --ink: #101828;
          --muted: #667085;
          --label: #475467;
          --field: #f5f6f8;
          --line: #ececf0;
        }
        .tv-prof * { box-sizing: border-box; }

        .tv-head { display: flex; align-items: center; gap: 22px; flex-wrap: wrap; }
        .tv-avatar {
          width: 84px; height: 84px; border-radius: 50%;
          background: var(--field); color: var(--muted);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .tv-name { font-size: 1.5rem; font-weight: 700; margin: 0; letter-spacing: -.02em; }
        .tv-uid { font-size: .85rem; color: var(--muted); margin: 2px 0 0; }

        .tv-badge {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: .76rem; font-weight: 700; padding: 4px 10px;
          border-radius: 999px; margin-top: 8px;
        }
        .tv-badge.ok { background: #e7f8f0; color: #027a48; }
        .tv-badge.pend { background: #fffaeb; color: #b54708; }

        .tv-panel {
          border: 1px solid var(--line); border-radius: 14px;
          padding: 20px 22px; margin-top: 24px; background: #fff;
        }
        .tv-panel-head {
          display: flex; justify-content: space-between; align-items: center;
          gap: 12px; margin-bottom: 14px; flex-wrap: wrap;
        }
        .tv-panel-title {
          font-size: .78rem; font-weight: 700; letter-spacing: .08em;
          text-transform: uppercase; color: var(--muted); margin: 0;
        }

        .tv-dl { margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 0 32px; }
        @media (max-width: 700px) { .tv-dl { grid-template-columns: 1fr; } }
        .tv-row {
          display: flex; justify-content: space-between; gap: 16px;
          padding: 11px 0; border-bottom: 1px solid var(--line);
        }
        .tv-row dt { font-size: .85rem; color: var(--muted); margin: 0; font-weight: 500; }
        .tv-row dd {
          font-size: .9rem; color: var(--ink); margin: 0; font-weight: 600;
          text-align: right; word-break: break-word;
        }
        .tv-empty { color: #b42318; font-weight: 500; font-style: italic; }

        .tv-btn {
          background: var(--accent); color: #fff; border: none;
          border-radius: 9px; padding: 9px 18px;
          font-size: .88rem; font-weight: 700; cursor: pointer;
          transition: background .15s;
        }
        .tv-btn:hover:not(:disabled) { background: var(--accent-strong); }
        .tv-btn:disabled { opacity: .55; cursor: not-allowed; }
        .tv-btn.ghost {
          background: #fff; color: var(--label); border: 1px solid #d0d5dd;
        }
        .tv-btn.ghost:hover { background: var(--field); }

        /* form */
        .tv-f { margin-bottom: 15px; }
        .tv-l { display: block; font-size: .85rem; font-weight: 600; color: var(--label); margin-bottom: 6px; }
        .tv-prof input.tv-i, .tv-prof select.tv-i {
          width: 100%; height: 46px;
          background: var(--field); border: 1px solid var(--line);
          border-radius: 10px; padding: 0 14px;
          font-size: .95rem; color: var(--ink); outline: none;
          transition: border-color .15s, box-shadow .15s, background .15s;
          -webkit-appearance: none; appearance: none;
        }
        .tv-prof select.tv-i {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23667085' stroke-width='1.8' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 14px center; padding-right: 36px;
        }
        .tv-prof input.tv-i:focus, .tv-prof select.tv-i:focus {
          background-color: #fff; border-color: var(--accent);
          box-shadow: 0 0 0 3px rgba(246,57,79,.14);
        }
        .tv-prof input.tv-i:disabled {
          background: #f2f4f7; color: var(--muted);
          -webkit-text-fill-color: var(--muted); cursor: not-allowed;
        }
        .tv-prof input.tv-i.bad, .tv-prof select.tv-i.bad { border-color: #fda29b; background: #fffbfa; }

        .tv-hint { font-size: .76rem; color: #98a2b3; margin: 5px 0 0; }
        .tv-err  { font-size: .76rem; color: #b42318; margin: 5px 0 0; font-weight: 500; }

        .tv-seg { display: flex; background: var(--field); border-radius: 10px; padding: 4px; gap: 4px; }
        .tv-seg button {
          flex: 1; border: none; background: none; padding: 9px 6px;
          border-radius: 7px; font-size: .87rem; font-weight: 600;
          color: var(--muted); cursor: pointer; transition: all .15s;
        }
        .tv-seg button.on { background: #fff; color: var(--accent); box-shadow: 0 1px 3px rgba(16,24,40,.12); }

        .tv-group { margin-bottom: 22px; }
        .tv-group-t {
          font-size: .74rem; font-weight: 700; letter-spacing: .08em;
          text-transform: uppercase; color: var(--muted);
          margin: 0 0 12px; padding-bottom: 8px; border-bottom: 1px solid var(--line);
        }
        .tv-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 0 14px; }
        @media (max-width: 560px) { .tv-2col { grid-template-columns: 1fr; } }

        .ProfileSection .modal-dialog.tv-dlg { max-width: 560px !important; width: 100% !important; }
        .tv-dlg .modal-content { border: none; border-radius: 14px; box-shadow: 0 16px 48px rgba(16,24,40,.2); }
        .tv-dlg .modal-header { padding: 16px 20px; border-bottom: 1px solid var(--line); }
        .tv-dlg .modal-body { padding: 20px; max-height: 68vh; overflow-y: auto; }
        .tv-dlg .modal-footer { padding: 14px 20px; border-top: 1px solid var(--line); }
        @media (max-width: 600px) {
          .ProfileSection .modal-dialog.tv-dlg { max-width: 94% !important; }
        }

        .tv-warn {
          display: flex; gap: 12px; align-items: flex-start;
          background: #fffaeb; border: 1px solid #fedf89; border-radius: 10px;
          padding: 12px 14px; font-size: .82rem; color: #93370d; margin-top: 4px;
        }
        .tv-warn a { color: #93370d; font-weight: 700; }

        .tv-danger { border-color: #fecdca; }
        .tv-danger .tv-panel-title { color: #b42318; }

        @media (prefers-reduced-motion: reduce) {
          .tv-prof *, .tv-prof *::before, .tv-prof *::after { transition: none !important; }
        }
      `}</style>

      <PageBanner title="Mi Cuenta" />
      <ToastContainer />

      <div className="px-4 profile_box mx-auto py-5 mt-10 bg-white rounded position-relative">
        <Link
          to={"/resetpassword"}
          style={{ position: "absolute", right: "5%", top: "3.5%" }}
          className="nav-link highlight"
        >
          Cambiar mi contraseña
        </Link>

        {/* ---- header ---- */}
        <div className="tv-head">
          <div className="tv-avatar">
            <FaUserAlt size={36} />
          </div>
          <div>
            <h1 className="tv-name">
              {userdata.name
                ? `${userdata.name} ${userdata.surname || ""}`.trim()
                : "Completa tu perfil"}
            </h1>
            <p className="tv-uid">Código de cliente {userdata.uid}</p>
            <span className={`tv-badge ${isComplete ? "ok" : "pend"}`}>
              {isComplete
                ? "Datos completos"
                : `${filled} de ${REQUIRED_FIELDS.length} datos completados`}
            </span>
          </div>
        </div>

        {/* ---- summary ---- */}
        <div className="tv-panel">
          <div className="tv-panel-head">
            <p className="tv-panel-title">Datos personales</p>
            <button className="tv-btn" onClick={() => setModal(true)}>
              <MdModeEdit style={{ marginRight: 6, marginTop: -2 }} />
              Editar datos
            </button>
          </div>

          <dl className="tv-dl">
            <Row label="Email" value={userdata.email} />
            <Row label="Teléfono" value={userdata.phone} />
            <Row label="Fecha de nacimiento" value={userdata.dob} />
            <Row label="Tipo de documento" value={userdata.doctype} />
            <Row label="Número de documento" value={userdata.docId} />
            <Row label="País de residencia" value={userdata.residence} />
            <Row label="Ocupación" value={userdata.occupation} />
            <Row label="Código postal" value={userdata.postalcode} />
          </dl>

          {!isComplete && (
            <div className="tv-warn mt-3">
              <span>
                Necesitamos todos tus datos antes de que puedas enviar dinero.
                Mantenlos actualizados en todo momento.
              </span>
            </div>
          )}
        </div>

        {/* ---- documento de identidad ---- */}
        <div className="tv-panel">
          <div className="tv-panel-head">
            <p className="tv-panel-title">Documento de identidad</p>
            <span className={`tv-badge ${docsOnFile ? "ok" : "pend"}`}>
              {docsOnFile ? "Recibido" : "Pendiente"}
            </span>
          </div>

          {docsOnFile ? (
            <p style={{ fontSize: ".88rem", color: "var(--muted)", margin: 0 }}>
              Tenemos una copia de tu{" "}
              <b style={{ color: "var(--ink)" }}>
                {DOC_LABELS[docStatus?.docType] || "documento"}
              </b>
              {docStatus?.back ? " (ambas caras)" : ""}. No necesitas volver a
              enviarla.
            </p>
          ) : (
            <>
              <p style={{ fontSize: ".88rem", color: "var(--muted)", margin: "0 0 14px" }}>
                Te pediremos una foto de tu documento la primera vez que envíes
                dinero. Solo una vez.
              </p>
              <button className="tv-btn ghost" onClick={() => navigate("/prealertar")}>
                Enviar dinero
              </button>
            </>
          )}
        </div>

        {/* ---- edit modal ---- */}
        {showModal && (
          <div className="modal profile_modal_bg d-block">
            <div className="modal-dialog tv-dlg mx-auto modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h1 className="modal-title fs-5">Tus datos personales</h1>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Cerrar"
                    onClick={() => {
                      setModal(false);
                      navigate("/profile");
                    }}
                  />
                </div>

                <div className="modal-body">
                  {/* --- identidad --- */}
                  <div className="tv-group">
                    <p className="tv-group-t">Identidad</p>

                    <div className="tv-2col">
                      <Field
                        error={errors.name}
                        id="name"
                        label="Nombres"
                        hint={
                          userdata?.name?.length > 0
                            ? "No se puede modificar"
                            : "Como en tu documento"
                        }
                      >
                        <input
                          type="text"
                          className={`tv-i${errors.name ? " bad" : ""}`}
                          id="name"
                          onChange={HandleChange}
                          value={data.name || ""}
                          disabled={userdata?.name?.length > 0}
                        />
                      </Field>

                      <Field
                        error={errors.surname}
                        id="surname"
                        label="Apellidos"
                        hint={
                          userdata?.surname?.length > 0
                            ? "No se puede modificar"
                            : "Como en tu documento"
                        }
                      >
                        <input
                          type="text"
                          className={`tv-i${errors.surname ? " bad" : ""}`}
                          id="surname"
                          onChange={HandleChange}
                          value={data.surname || ""}
                          disabled={userdata?.surname?.length > 0}
                        />
                      </Field>
                    </div>

                    <div className="tv-2col">
                      <Field
                        error={errors.dob} id="dob" label="Fecha de nacimiento" hint="DD-MM-AAAA">
                        <input
                          type="text"
                          className={`tv-i${errors.dob ? " bad" : ""}`}
                          id="dob"
                          inputMode="numeric"
                          placeholder="01-01-1990"
                          onChange={HandleDOBChange}
                          value={data.dob || ""}
                          autoComplete="off"
                        />
                      </Field>

                      <Field
                        error={errors.phone} id="phone" label="Teléfono" hint="Con prefijo si es extranjero">
                        <input
                          type="tel"
                          className={`tv-i${errors.phone ? " bad" : ""}`}
                          id="phone"
                          inputMode="tel"
                          placeholder="600 000 000"
                          onChange={HandleChange}
                          value={data.phone || ""}
                        />
                      </Field>
                    </div>

                    <div className="tv-f">
                      <label className="tv-l">Tipo de documento</label>
                      <div className="tv-seg">
                        {["DNI", "NIE", "Pasaporte"].map((t) => (
                          <button
                            key={t}
                            type="button"
                            className={data.doctype === t ? "on" : ""}
                            onClick={() => {
                              setData({ ...data, doctype: t });
                              setErrors((p) => ({ ...p, doctype: null }));
                            }}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                      {errors.doctype ? (
                        <p className="tv-err">{errors.doctype}</p>
                      ) : (
                        <p className="tv-hint">
                          Si tu NIE no lleva fotografía, elige Pasaporte.
                        </p>
                      )}
                    </div>

                    <Field
                        error={errors.docId}
                      id="docId"
                      label="Número de documento"
                      hint={userdata?.docId?.length > 0 ? "No se puede modificar" : null}
                    >
                      <input
                        type="text"
                        className={`tv-i${errors.docId ? " bad" : ""}`}
                        id="docId"
                        onChange={HandleChange}
                        value={data?.docId || ""}
                        disabled={userdata?.docId?.length > 0}
                      />
                    </Field>
                  </div>

                  {/* --- dirección --- */}
                  <div className="tv-group">
                    <p className="tv-group-t">Dirección</p>

                    <div className="tv-2col">
                      <Field
                        error={errors.direction} id="direction" label="Calle">
                        <input
                          type="text"
                          className={`tv-i${errors.direction ? " bad" : ""}`}
                          id="direction"
                          onChange={HandleChange}
                          value={data?.direction || ""}
                        />
                      </Field>

                      <Field
                        error={errors.homenumber} id="homenumber" label="Número">
                        <input
                          type="text"
                          className={`tv-i${errors.homenumber ? " bad" : ""}`}
                          id="homenumber"
                          onChange={HandleChange}
                          value={data?.homenumber || ""}
                        />
                      </Field>
                    </div>

                    <Field
                        error={errors.homevia} id="homevia" label="Población y provincia">
                      <input
                        type="text"
                        className={`tv-i${errors.homevia ? " bad" : ""}`}
                        id="homevia"
                        placeholder="Madrid, Madrid"
                        onChange={HandleChange}
                        value={data?.homevia || ""}
                      />
                    </Field>

                    <div className="tv-2col">
                      <Field
                        error={errors.postalcode} id="postalcode" label="Código postal">
                        <input
                          type="text"
                          className={`tv-i${errors.postalcode ? " bad" : ""}`}
                          id="postalcode"
                          inputMode="numeric"
                          onChange={HandleChange}
                          value={data?.postalcode || ""}
                        />
                      </Field>

                      <Field
                        error={errors.residence} id="residence" label="País">
                        <select
                          className={`tv-i${errors.residence ? " bad" : ""}`}
                          id="residence"
                          value={data.residence || ""}
                          onChange={HandleChange}
                        >
                          <option value="">Elige un país</option>
                          {[...countries].sort().map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  </div>

                  {/* --- ocupación --- */}
                  <div className="tv-group" style={{ marginBottom: 0 }}>
                    <p className="tv-group-t">Ocupación</p>
                    <Field
                        error={errors.occupation}
                      id="occupation"
                      label="¿A qué te dedicas?"
                      hint="Nos lo exige la normativa de prevención de blanqueo."
                    >
                      <select
                        className={`tv-i${errors.occupation ? " bad" : ""}`}
                        id="occupation"
                        value={data.occupation || ""}
                        onChange={HandleChange}
                      >
                        <option value="">Elige una ocupación</option>
                        {[...occupations].sort().map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="tv-btn ghost"
                    onClick={() => setModal(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="tv-btn ms-2"
                    onClick={HandleEdit}
                    disabled={loading}
                  >
                    {loading ? "Guardando…" : "Guardar datos"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ---- delete ---- */}
      <div className="profile_box mx-auto mb-5">
        <div className="tv-panel tv-danger">
          <p className="tv-panel-title">Eliminar tu cuenta</p>
          <p style={{ fontSize: ".88rem", color: "var(--muted)", margin: "0 0 14px" }}>
            Tu cuenta y toda la información vinculada se eliminarán de forma
            permanente. Esta acción no se puede deshacer.
          </p>
          <button className="tv-btn ghost" onClick={DeleteAcc}>
            Eliminar cuenta
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;